import { useRef, useState, useCallback, useEffect } from "react";
import beforeJpg from "@/assets/garage-before.jpg";
import beforeWebp from "@/assets/garage-before.webp";
import beforeWebpSm from "@/assets/garage-before-sm.webp";
import afterJpg from "@/assets/garage-after.jpg";
import afterWebp from "@/assets/garage-after.webp";
import afterWebpSm from "@/assets/garage-after-sm.webp";

export const BeforeAfter = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState(50);
  const dragging = useRef(false);

  const setFromClientX = useCallback((clientX: number) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const p = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, p)));
  }, []);

  useEffect(() => {
    const move = (e: MouseEvent | TouchEvent) => {
      if (!dragging.current) return;
      const x = "touches" in e ? e.touches[0].clientX : e.clientX;
      setFromClientX(x);
    };
    const up = () => (dragging.current = false);
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    window.addEventListener("touchmove", move);
    window.addEventListener("touchend", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("touchend", up);
    };
  }, [setFromClientX]);

  return (
    <div
      ref={ref}
      className="relative mx-auto aspect-[16/9] w-full max-w-4xl select-none overflow-hidden rounded-xl border-2 border-gold/40 shadow-elevated"
      onMouseDown={(e) => { dragging.current = true; setFromClientX(e.clientX); }}
      onTouchStart={(e) => { dragging.current = true; setFromClientX(e.touches[0].clientX); }}
    >
      <picture>
        <source media="(max-width: 640px)" srcSet={afterWebpSm} type="image/webp" />
        <source srcSet={afterWebp} type="image/webp" />
        <img src={afterJpg} alt="Edmonton garage after a Garage Kings YEG reset and junk removal" className="absolute inset-0 h-full w-full object-cover" loading="lazy" decoding="async" width={1600} height={900} />
      </picture>
      <div className="absolute inset-0 overflow-hidden" style={{ width: `${pos}%` }}>
        <picture>
          <source media="(max-width: 640px)" srcSet={beforeWebpSm} type="image/webp" />
          <source srcSet={beforeWebp} type="image/webp" />
          <img src={beforeJpg} alt="Cluttered Edmonton garage before professional junk removal" className="absolute inset-0 h-full w-full object-cover" style={{ width: `${100 / (pos / 100)}%`, maxWidth: "none" }} loading="lazy" decoding="async" width={1600} height={900} />
        </picture>
      </div>
      <div className="pointer-events-none absolute left-4 top-4 rounded bg-ink/70 px-3 py-1 font-display text-lg tracking-widest text-gold">BEFORE</div>
      <div className="pointer-events-none absolute right-4 top-4 rounded bg-ink/70 px-3 py-1 font-display text-lg tracking-widest text-gold">AFTER</div>
      <div
        className="absolute inset-y-0 w-1 bg-gold shadow-[0_0_20px_hsl(var(--gold))]"
        style={{ left: `calc(${pos}% - 2px)` }}
      >
        <div className="absolute top-1/2 left-1/2 flex h-12 w-12 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-4 border-gold bg-ink text-gold shadow-gold cursor-ew-resize">
          ⇆
        </div>
      </div>
    </div>
  );
};
